import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import { usePostHog } from "@posthog/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { sendMessageSchema, type SendMessageFormValues } from "@/lib/schemas"
import { ApplicationsQuery, SendMessageMutation } from "@/lib/graphql/outbound"

interface SendMessageFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function SendMessageForm({ open, onOpenChange, onSuccess }: SendMessageFormProps) {
  const posthog = usePostHog()
  const [, send] = useMutation(SendMessageMutation)
  const [apps] = useQuery({ query: ApplicationsQuery, variables: { limit: 100 } })

  const form = useForm<SendMessageFormValues>({
    resolver: zodResolver(sendMessageSchema) as Resolver<SendMessageFormValues>,
    defaultValues: {
      applicationId: "",
      eventType: "",
      payload: "",
      idempotencyKey: "",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        applicationId: "",
        eventType: "",
        payload: "",
        idempotencyKey: "",
      })
    }
  }, [open, form])

  const onSubmit = async (values: SendMessageFormValues) => {
    let payload: string
    try {
      JSON.parse(values.payload)
      payload = btoa(values.payload)
    } catch {
      form.setError("payload", { message: "Invalid JSON" })
      return
    }

    const result = await send({
      input: {
        applicationId: values.applicationId,
        eventType: values.eventType,
        payload,
        idempotencyKey: values.idempotencyKey || undefined,
      },
    })

    if (result.error) {
      toast.error(result.error.message)
      return
    }

    posthog?.capture("message_sent", { application_id: values.applicationId, event_type: values.eventType })
    toast.success("Message sent")
    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Message</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="applicationId">Application</Label>
              <Select
                value={form.watch("applicationId")}
                onValueChange={(v) => form.setValue("applicationId", v, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select application" />
                </SelectTrigger>
                <SelectContent>
                  {apps.data?.applications.nodes.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={form.formState.errors.applicationId} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventType">Event Type</Label>
              <Input
                id="eventType"
                className="font-mono text-sm"
                placeholder="invoice.paid"
                {...form.register("eventType")}
              />
              <FieldError error={form.formState.errors.eventType} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payload">Payload (JSON)</Label>
              <Textarea
                id="payload"
                className="font-mono text-xs min-h-25"
                placeholder='{"key": "value"}'
                {...form.register("payload")}
              />
              <FieldError error={form.formState.errors.payload} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="idempotencyKey">
                Idempotency Key <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="idempotencyKey"
                className="font-mono text-sm"
                placeholder="Auto-generated if empty"
                {...form.register("idempotencyKey")}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Send"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
